/**
 * GuestStrategyView — /dashboard/strategy for a Guest (a brand's client).
 *
 * Guests have no strategy of their own: they read the ones an operator sent
 * them (ShareStrategyModal), view only, on the same deck the team uses
 * (Figma 13, "The client reads" 72:4989, comps C 62:11695), with every edit
 * callback left out and the story only. Comments, Approve, Ask for changes and
 * Export PDF are drawn in the design but have no backend, so they are not
 * here. The share email links here with `?strategy=`.
 *
 * Self-contained with the backend's StrategyGuestShare and /guest-strategy so
 * the feature can be removed as one unit (backend docs/guest-role-plan.md §5c).
 */
import { useMemo } from "react";
import { useSearchParams } from "react-router";
import { Check, FileX2 } from "lucide-react";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useAgentFocus } from "../../agent/agentFocusStore";
import { KitMenu, KitMenuItem, KitMenuPopover, KitMenuTrigger, KitPressable } from "../../components/kit/Menu";
import { EmptyState, ErrorState } from "../../components/ViewStates";
import StrategyDocumentView, { type DeckView } from "../components/StrategyDocumentView";
import { DeckStatus, StrategyLoading } from "../deck/DeckParts";
import { dayMonthYear } from "../deck/deckModel";
import { isGuestStrategyGone, useGuestStrategies, useGuestStrategy } from "./useGuestStrategy";
import "../deck/deck.css";

export default function GuestStrategyView() {
  const brandId = useWorkspaceStore((s) => s.selectedBrandId);
  const [searchParams, setSearchParams] = useSearchParams();
  const list = useGuestStrategies(brandId);
  // The deck manages its own scroll regions; the page must not also scroll.
  useAgentFocus({ surface: "strategy" }, { contained: true });

  const strategies = useMemo(() => list.data ?? [], [list.data]);
  const wanted = searchParams.get("strategy");
  const selected = strategies.find((s) => s.id === wanted) ?? strategies[0] ?? null;
  const deck = useGuestStrategy(brandId, selected?.id ?? null);
  const viewParam = searchParams.get("view");
  const view: DeckView = viewParam === "grid" ? "grid" : "slides";

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value == null) next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  if (!brandId) {
    return <div className="sd-start"><EmptyState title="Pick a brand" body="Choose a brand to see the strategies shared with you." /></div>;
  }
  if (list.isLoading) return <StrategyLoading />;
  if (list.isError) {
    return (
      <div className="sd-start">
        <ErrorState title="The strategies did not load" body="Nothing was shown, so nothing you read has changed." onRetry={() => void list.refetch()} />
      </div>
    );
  }
  if (!selected) {
    return (
      <div className="sd-start">
        <EmptyState title="No strategy shared with you yet" body="When the team sends you a strategy for this brand, it shows up here for you to read." />
      </div>
    );
  }
  if (deck.isLoading) return <StrategyLoading />;
  if (isGuestStrategyGone(deck.error)) {
    return (
      <div className="sd-start">
        <EmptyState
          icon={<FileX2 aria-hidden />}
          title="This strategy is no longer shared with you"
          body={`The team stopped sharing ${selected.title} with you, or replaced it with a newer version. If you need it again, ask the team that sent it.`}
        />
      </div>
    );
  }
  if (deck.isError || !deck.data) {
    return (
      <div className="sd-start">
        <ErrorState title="This strategy did not load" body="Nothing was shown, so nothing you read has changed." onRetry={() => void deck.refetch()} />
      </div>
    );
  }

  const data = deck.data;
  const version = /^v(\d+)$/.exec(data.strategy.version)?.[1];

  return (
    <StrategyDocumentView
      key={data.strategy.id}
      view={view}
      onViewChange={(next) => update({ view: next })}
      selectedSlideId={searchParams.get("slide")}
      onSlideChange={(slide) => { if (searchParams.get("slide") !== slide.id) update({ slide: slide.id }); }}
      doc={data.strategy.document ?? {}}
      meta={{
        brand: data.brand.name,
        title: data.strategy.title,
        periodStart: data.strategy.periodStart,
        periodEnd: data.strategy.periodEnd,
      }}
      slideImages={data.slideImages}
      pictureUrls={data.pictureUrls}
      toolbar={() => (
        <div className="sd-top">
          {strategies.length > 1 ? (
            <KitMenuTrigger>
              <KitPressable>
                <button type="button" className="sd-title" aria-label={`${data.strategy.title}. Strategies shared with you`}>{data.strategy.title}</button>
              </KitPressable>
              <KitMenuPopover placement="bottom start" className="min-w-[320px]">
                <KitMenu aria-label="Strategies shared with you" onAction={(key) => update({ strategy: String(key), slide: null })}>
                  {strategies.map((s) => (
                    <KitMenuItem key={s.id} id={s.id} textValue={`${s.title} · ${s.period}`}>
                      <span className="flex items-center gap-2"><span className="min-w-0 flex-1 truncate">{s.title} · {s.period}</span>{s.id === selected.id ? <Check aria-hidden className="size-3 text-co-accent" /> : null}</span>
                    </KitMenuItem>
                  ))}
                </KitMenu>
              </KitMenuPopover>
            </KitMenuTrigger>
          ) : (
            <h1 className="sd-title" style={{ cursor: "default" }}>{data.strategy.title}</h1>
          )}
          {version ? <DeckStatus>VERSION {version}</DeckStatus> : null}
          <span className="sd-meta">Sent on {dayMonthYear(selected.sentAt)}</span>
        </div>
      )}
    />
  );
}
